import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SpinnerGap, Warning, WarningCircle, BellRinging, Cake, FunnelSimple, Users, CalendarCheck } from '@phosphor-icons/react';

const SEV_STYLES = {
  high: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-[#FF2B2B]', label: 'URGENTE', icon: WarningCircle },
  medium: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', label: 'ATENÇÃO', icon: Warning },
  low: { bg: 'bg-blue-50', border: 'border-[#0F1829]/20', text: 'text-[#0F1829]', label: 'INFO', icon: BellRinging },
};
const TIPO_ICONS = { lead_parada: FunnelSimple, consultor_sem_enc: Users, aniversario: Cake, leads_parados_semanal: CalendarCheck };

export default function AlertsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterSev, setFilterSev] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [preview, setPreview] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.ID_Cliente === 1;

  useEffect(() => {
    setLoading(true);
    const qs = preview ? '?preview=1' : '';
    api.post(`/alertas/all${qs}`).then(r => { setData(r.data); setLoading(false); }).catch((e) => { console.error(e); setLoading(false); });
  }, [preview]);

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>;
  if (!data) return null;

  const alertas = (data.alertas || []).filter(a => (!filterSev || a.severity === filterSev) && (!filterTipo || a.tipo === filterTipo));

  return (
    <div data-testid="alerts-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Alertas Inteligentes</h1>
          <p className="text-sm text-[#6B7280] mt-1">Notificações proativas baseadas nos dados do SQL Server</p>
        </div>
        {isAdmin && (
          <label className="flex items-center gap-2 text-xs text-[#6B7280] cursor-pointer select-none" title="Força alertas semanais a aparecer em qualquer dia (admin)">
            <input type="checkbox" data-testid="alert-preview-toggle" checked={preview} onChange={e => setPreview(e.target.checked)}
              className="w-4 h-4 accent-[#0F1829]" />
            Simular 2ª feira (preview)
          </label>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <button onClick={() => setFilterSev('')} className={`bg-white border rounded-lg p-4 text-center transition-all hover:-translate-y-0.5 hover:shadow-md ${!filterSev ? 'border-[#0F1829] ring-2 ring-[#0F1829]/10' : 'border-[#E5E7EB]'}`}>
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Total</p>
          <p className="text-3xl font-bold font-mono text-[#111827] mt-1">{data.summary.total}</p>
        </button>
        <button onClick={() => setFilterSev(filterSev === 'high' ? '' : 'high')} className={`bg-white border rounded-lg p-4 text-center transition-all hover:-translate-y-0.5 hover:shadow-md ${filterSev === 'high' ? 'border-[#FF2B2B] ring-2 ring-red-100' : 'border-[#E5E7EB]'}`}>
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#FF2B2B]">Urgente</p>
          <p className="text-3xl font-bold font-mono text-[#FF2B2B] mt-1">{data.summary.high}</p>
        </button>
        <button onClick={() => setFilterSev(filterSev === 'medium' ? '' : 'medium')} className={`bg-white border rounded-lg p-4 text-center transition-all hover:-translate-y-0.5 hover:shadow-md ${filterSev === 'medium' ? 'border-amber-500 ring-2 ring-amber-100' : 'border-[#E5E7EB]'}`}>
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-amber-600">Atenção</p>
          <p className="text-3xl font-bold font-mono text-amber-600 mt-1">{data.summary.medium}</p>
        </button>
        <button onClick={() => setFilterSev(filterSev === 'low' ? '' : 'low')} className={`bg-white border rounded-lg p-4 text-center transition-all hover:-translate-y-0.5 hover:shadow-md ${filterSev === 'low' ? 'border-[#0F1829] ring-2 ring-blue-100' : 'border-[#E5E7EB]'}`}>
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#0F1829]">Info</p>
          <p className="text-3xl font-bold font-mono text-[#0F1829] mt-1">{data.summary.low}</p>
        </button>
      </div>

      {/* Filter by type */}
      <div className="flex gap-2 mb-4">
        {[{key:'',label:'Todos'},{key:'lead_parada',label:'Leads Paradas'},{key:'consultor_sem_enc',label:'Consultores s/ Enc.'},{key:'aniversario',label:'Aniversários'}].map(t => (
          <button key={t.key} onClick={() => setFilterTipo(filterTipo === t.key ? '' : t.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${filterTipo === t.key ? 'bg-[#0F1829] text-white border-[#0F1829]' : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:bg-[#F3F4F6]'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Alert list */}
      <div className="space-y-2">
        {alertas.map((a, i) => {
          const s = SEV_STYLES[a.severity] || SEV_STYLES.low;
          const TipoIcon = TIPO_ICONS[a.tipo] || BellRinging;
          const hasLeadsList = a.tipo === 'leads_parados_semanal' && Array.isArray(a.leads) && a.leads.length > 0;
          return (
            <div key={`${a.tipo || 'alert'}-${a.id ?? ''}-${a.titulo ?? i}`} className={`${s.bg} border ${s.border} rounded-lg p-4 hover:shadow-sm transition-all ${!hasLeadsList ? 'cursor-pointer flex items-start gap-3' : ''}`}
              onClick={() => { if (a.tipo === 'lead_parada') navigate(`/negocios/${a.id}`); }}>
              {!hasLeadsList && (
                <>
                  <div className="p-1.5 rounded-md bg-white/80 flex-shrink-0">
                    <TipoIcon size={18} className={s.text} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-[8px] uppercase tracking-[0.2em] font-bold ${s.text}`}>{s.label}</span>
                      <span className="text-xs font-semibold text-[#111827]">{a.titulo}</span>
                    </div>
                    <p className="text-sm text-[#6B7280]">{a.descricao}</p>
                  </div>
                  {a.data && <span className="text-[10px] text-[#6B7280] flex-shrink-0">{a.data}</span>}
                </>
              )}
              {hasLeadsList && (
                <>
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 rounded-md bg-white/80 flex-shrink-0">
                      <TipoIcon size={18} className={s.text} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className={`text-[8px] uppercase tracking-[0.2em] font-bold ${s.text}`}>{s.label}</span>
                        <span className="text-xs font-semibold text-[#111827]">{a.titulo}</span>
                      </div>
                      <p className="text-sm text-[#6B7280]">{a.descricao}</p>
                    </div>
                    {a.data && <span className="text-[10px] text-[#6B7280] flex-shrink-0">{a.data}</span>}
                  </div>
                  <div className="mt-3 bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="weekly-leads-table">
                        <thead className="bg-[#F9FAFB] text-[#6B7280] uppercase tracking-wide">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">ID</th>
                            <th className="px-3 py-2 text-left font-medium">Tipo</th>
                            <th className="px-3 py-2 text-left font-medium">Origem</th>
                            <th className="px-3 py-2 text-left font-medium">Cliente</th>
                            <th className="px-3 py-2 text-left font-medium">Último Estado</th>
                            <th className="px-3 py-2 text-right font-medium">Dias</th>
                            <th className="px-3 py-2 text-left font-medium">Última Atividade</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E5E7EB]">
                          {a.leads.map(lead => (
                            <tr key={lead.id} className="hover:bg-[#F9FAFB] cursor-pointer" onClick={() => navigate(`/negocios/${lead.id}`)} data-testid={`weekly-lead-${lead.id}`}>
                              <td className="px-3 py-2 font-medium text-[#111827]">#{lead.id}</td>
                              <td className="px-3 py-2 text-[#374151]">{lead.tipoLead}</td>
                              <td className="px-3 py-2 text-[#374151]">{lead.origemLead}</td>
                              <td className="px-3 py-2 text-[#374151] truncate max-w-[180px]">{lead.cliente}</td>
                              <td className="px-3 py-2 text-[#6B7280]">{lead.ultimoEstado}</td>
                              <td className={`px-3 py-2 text-right font-semibold ${lead.dias > 30 ? 'text-red-600' : lead.dias > 14 ? 'text-orange-500' : 'text-[#E65100]'}`}>{lead.dias}</td>
                              <td className="px-3 py-2 text-[#6B7280] whitespace-nowrap">{lead.ultimaAtividade || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      {alertas.length === 0 && <div className="text-center py-12 text-[#6B7280]">Sem alertas para os filtros selecionados</div>}
    </div>
  );
}
